import PanelSlider from '../../../smartpak/panelslider.v3';
import {debounce} from '../../../utils/debounce';
import { build2xResponsiveImage } from '../shared/imagehelper';
import imageZoom from '../shared/imagezoom';


const THUMBNAIL_WIDTH = 102;
const IMAGE_WIDTH = 510;

class ImageControlLarge {
    constructor($container, productClassViewModel) {
        if (!$container || !productClassViewModel) {
            throw new Error('ImageControlLarge was called without required parameters');
        }

        this.$container = $container;
        this.viewmodel = productClassViewModel;
        this.imageMap = new Map();

        const $fragment = document.createDocumentFragment();

        this.$imageLink = document.createElement('a');

        this.$imageLink.href = '#';
        this.$imageLink.classList.add('zoomable');
        this.$imageLink.setAttribute('aria-label', 'Show product image');

        this.$image = build2xResponsiveImage({
            url: this.viewmodel.selectedImage()?.url(),
            alt: '',
            lazy: false,
            width: IMAGE_WIDTH,
            height: IMAGE_WIDTH
        });

        this.setSelectedImage(this.viewmodel.selectedImage());

        this.$imageLink.appendChild(this.$image);

        this.$screenReaderImageName = document.createElement('span');
        this.$screenReaderImageName.classList.add('sr-only');
        this.$screenReaderImageName.innerText = this.viewmodel.name();

        this.$imageLink.appendChild(this.$screenReaderImageName);

        $fragment.appendChild(this.$imageLink);

        if(this.viewmodel.carouselImages().length){
            $fragment.appendChild(buildScreenReaderMessage());
        }

        this.$thumbnailContainer = document.createElement('div');
        this.$thumbnailContainer.id = 'imageThumbCarousel';

        this.$thumbnailList = document.createElement('ul');

        if(this.viewmodel.imageGroups.length){
            this.$thumbnailList.classList.add('noControls');
        }

        this.$thumbnailContainer.appendChild(this.$thumbnailList);

        this.setThumbnails(this.viewmodel.carouselImages());

        $fragment.appendChild(this.$thumbnailContainer);

        this.$container.innerHTML = '';
        this.$container.appendChild($fragment);

        imageZoom.init(this.$container);

        this.imageCarousel = new PanelSlider(this.$thumbnailList);

        this.$thumbnailList.addEventListener('click', e => this.handleClick(e));

        this.selectedImageSubscription = this.viewmodel.selectedImage.subscribe(image => {
            this.setSelectedImage(image);
            this.imageCarousel.refresh();
        });

        this.carouselImagesSubscription = this.viewmodel.carouselImages.subscribe(carouselImages => {
            this.setThumbnails(carouselImages);
            this.setSelectedImage(carouselImages[0]);
            this.imageCarousel.refresh();
        });

        this._debounceRefresh = debounce(() => this.imageCarousel.refresh());

        window.addEventListener('resize', this._debounceRefresh);
    }

    handleClick(event){
        this.viewmodel.selectedImage(this.imageMap.get(event.target));
        this.$imageLink.focus();
    }

    setThumbnails(thumbnails){
        this.imageMap.clear();

        this.$thumbnailList.innerHTML = '';

        const $listFragment = document.createDocumentFragment();

        thumbnails.forEach(thumbnail => {
            const $listItem = document.createElement('li'),
                $img = build2xResponsiveImage({
                    lazy: true,
                    url: thumbnail.url(),
                    width: THUMBNAIL_WIDTH,
                    height: THUMBNAIL_WIDTH,
                    alt: `${this.viewmodel.name()} - ${thumbnail.attributeString()}`
                });

            $listItem.appendChild($img);
            $listFragment.appendChild($listItem);

            this.imageMap.set($img, thumbnail);
        });

        this.$thumbnailList.appendChild($listFragment);
    }

    setSelectedImage(image){
        if (!image) {
            return;
        }

        const url = image.url();

        if (this.$image.dataset.image !== url) {
            this.$image.dataset.image = url;
        }

        if (this.$image.src.includes(url)) {
            return;
        }

        this.$image.setAttribute('srcset', `${url}?w=${IMAGE_WIDTH} 1x, ${url}?w=${IMAGE_WIDTH * 2} 2x`);
        this.$image.src = `${url}?width=${IMAGE_WIDTH}`;           
    }

    destroy() {
        this.imageMap.clear();
        window.removeEventListener('resize', this._debounceRefresh);
        this.$thumbnailList.removeEventListener('click', e => this.handleClick(e));
        this.carouselImagesSubscription.dispose();
        this.selectedImageSubscription.dispose();
        this.$container.innerHTML = '';
    }
}

function buildScreenReaderMessage(){
    const $element = document.createElement('div');

    $element.className = 'sr-only';
    $element.innerText = 'Clicking on the following image links will update the image above.';

    return $element;
}

export default ImageControlLarge;